import { Injectable } from '@angular/core';
import {
  HttpClient,
  HttpHeaders,
  HttpParams,
  HttpResponse,
} from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';

@Injectable({
  providedIn: 'root',
})
export class HttpService {
  constructor(private readonly http: HttpClient) {}

  private getRequestHeaders(): HttpHeaders {
    return new HttpHeaders({
      'Content-Type': 'application/json',
    });
  }

  private toHttpParams(
    params?: HttpParams | Record<string, string | number | boolean>,
  ): HttpParams | undefined {
    if (!params) {
      return undefined;
    }
    if (params instanceof HttpParams) {
      return params;
    }
    let httpParams = new HttpParams();
    for (const [key, value] of Object.entries(params)) {
      if (value !== undefined && value !== null && value !== '') {
        httpParams = httpParams.set(key, String(value));
      }
    }
    return httpParams;
  }

  get<T>(
    url: string,
    params?: HttpParams | Record<string, string | number | boolean>,
    options?: { workspaceId?: string },
  ): Observable<T> {
    let headers = this.getRequestHeaders();
    const workspaceId = options?.workspaceId?.trim();
    if (workspaceId) {
      headers = headers.set('workspace-id', workspaceId);
    }
    const requestOptions = {
      headers,
      params: this.toHttpParams(params),
    };
    return this.http.get<T>(`${environment.apiUrl}${url}`, requestOptions);
  }

  post<T>(url: string, body: any, params?: HttpParams | Record<string, string | number | boolean>): Observable<T> {
    const options = {
      headers: this.getRequestHeaders(),
      params: this.toHttpParams(params),
    };
    return this.http.post<T>(`${environment.apiUrl}${url}`, body, options);
  }

  put<T>(url: string, body: any, params?: HttpParams | Record<string, string | number | boolean>): Observable<T> {
    const options = {
      headers: this.getRequestHeaders(),
      params: this.toHttpParams(params),
    };
    return this.http.put<T>(`${environment.apiUrl}${url}`, body, options);
  }

  patch<T>(url: string, body: any, params?: HttpParams | Record<string, string | number | boolean>): Observable<T> {
    const options = {
      headers: this.getRequestHeaders(),
      params: this.toHttpParams(params),
    };
    return this.http.patch<T>(`${environment.apiUrl}${url}`, body, options);
  }

  delete<T>(url: string, params?: HttpParams | Record<string, string | number | boolean>): Observable<T> {
    const options = {
      headers: this.getRequestHeaders(),
      params: this.toHttpParams(params),
    };
    return this.http.delete<T>(`${environment.apiUrl}${url}`, options);
  }

  getBlob(url: string): Observable<Blob> {
    return this.http.get(`${environment.apiUrl}${url}`, { responseType: 'blob' });
  }

  getBlobResponse(url: string): Observable<HttpResponse<Blob>> {
    return this.http.get(`${environment.apiUrl}${url}`, {
      responseType: 'blob',
      observe: 'response',
    });
  }
}
